import { registerPlugin } from "@capacitor/core";
import { readNativeFile } from "./files";
import { getNativePlatform } from "./platform";

/** iOS: own VisionKit plugin in AppDelegate.swift. Empty result = cancelled. */
interface HvDocumentScannerPlugin {
  scan(): Promise<{ readonly pdfPath?: string }>;
}

let iosScanner: HvDocumentScannerPlugin | null = null;

/** Lazy: module is imported during SSR, plugin proxy only makes sense in the shell. */
function getIosScanner(): HvDocumentScannerPlugin {
  iosScanner ??= registerPlugin<HvDocumentScannerPlugin>("HvDocumentScanner");
  return iosScanner;
}

/** Scanner only exists in the store apps. */
export function canScanDocuments(): boolean {
  return getNativePlatform() !== "web";
}

/**
 * System document scanner (VisionKit / ML Kit) → one PDF.
 * Null when cancelled; throws when the scanner fails.
 */
export async function scanDocumentToPdf(
  fileName: string,
): Promise<File | null> {
  const platform = getNativePlatform();
  let uri: string | undefined;
  if (platform === "ios") {
    uri = (await getIosScanner().scan()).pdfPath;
  } else if (platform === "android") {
    const { DocumentScanner } = await import(
      "@capacitor-mlkit/document-scanner"
    );
    try {
      const result = await DocumentScanner.scanDocument({
        galleryImportAllowed: false,
        pageLimit: 24,
        resultFormats: "PDF",
        scannerMode: "FULL",
      });
      uri = result.pdf?.uri;
    } catch (error) {
      // ML Kit rejects on back/close; that is a cancel, not a failure.
      if (String(error).toLowerCase().includes("cancel")) return null;
      throw error;
    }
  } else {
    return null;
  }
  if (!uri) return null;
  const file = await readNativeFile(uri, fileName, "application/pdf");
  if (!file) throw new Error("Scanned PDF could not be read");
  return file;
}
